import type { IconType } from "react-icons";
import {
  FiArchive,
  FiAward,
  FiBarChart2,
  FiBookOpen,
  FiBriefcase,
  FiCheckCircle,
  FiCheckSquare,
  FiEdit,
  FiFileText,
  FiFolder,
  FiGlobe,
  FiGrid,
  FiHome,
  FiInbox,
  FiLayers,
  FiList,
  FiMessageSquare,
  FiPlusCircle,
  FiSearch,
  FiShoppingCart,
  FiUsers,
} from "react-icons/fi";
import { urls } from "../../../urls";
import type { SubAccess } from "../../subscription/useSub";
import { type BucketBadgeKey, bucketQuery } from "./statusBuckets";

export type AppRole = "advisor" | "orderer" | "supplier";

// Needs (behov*) badges plus the procurement bucket badges. Bucket badges are owned by
// statusBuckets so BUCKET_META can't assign a Needs key to a bucket.
export type NavBadgeKey = "behovInnmeldt" | "behovGodkjent" | BucketBadgeKey;

export interface NavItem {
  icon: IconType;
  labelKey: string;
  path: string;
  query?: string;
  roles: AppRole[];
  badgeKey?: NavBadgeKey;
  /** Gate a single item on a `useSub()` flag (in addition to its section's `access`). */
  access?: keyof SubAccess;
}

export type SectionActionKind = "createSourcingEvent" | "createContract" | "createProject";

export interface NavSectionAction {
  labelKey: string;
  roles: AppRole[];
  kind: SectionActionKind;
  /** Gate the action on a `useSub()` flag (e.g. hide "New contract" from view-only roles). */
  access?: keyof SubAccess;
}

export interface NavSection {
  titleKey: string;
  /** Identifies the section on its (collapsible) header row, also when collapsed. */
  icon: IconType;
  items: NavItem[];
  action?: NavSectionAction;
  /** Gate on a `useSub()` flag — subscription access or, for admin-only sections, `isOrgAdmin`. */
  access?: keyof SubAccess;
}

const BUYER_ROLES: AppRole[] = ["advisor", "orderer"];
const ALL_ROLES: AppRole[] = ["advisor", "orderer", "supplier"];

export const navConfig: NavSection[] = [
  {
    titleKey: "Overview",
    icon: FiHome,
    items: [{ icon: FiHome, labelKey: "Overview", path: urls.v2.home, roles: ALL_ROLES }],
  },
  {
    titleKey: "Assistant",
    icon: FiMessageSquare,
    access: "hasAssistantAccess",
    items: [{ icon: FiMessageSquare, labelKey: "Assistant", path: urls.v2.assistant, roles: ALL_ROLES }],
  },
  {
    titleKey: "Needs",
    icon: FiInbox,
    access: "hasNeedsAccess",
    items: [
      { icon: FiPlusCircle, labelKey: "New need", path: urls.v2.needs.new, roles: BUYER_ROLES, access: "canReportNeeds" },
      { icon: FiInbox, labelKey: "Inbox", path: urls.v2.needs.list, query: "status=REPORTED", roles: ["advisor"], badgeKey: "behovInnmeldt", access: "canAssessNeeds" },
      { icon: FiList, labelKey: "My needs", path: urls.v2.needs.mine, roles: BUYER_ROLES, access: "canReportNeeds" },
      { icon: FiInbox, labelKey: "Needs overview", path: urls.v2.needs.list, roles: ["advisor"], access: "canAssessNeeds" },
      { icon: FiCheckCircle, labelKey: "Approved needs", path: urls.v2.needs.list, query: "status=APPROVED", roles: ["advisor"], badgeKey: "behovGodkjent", access: "canAssessNeeds" },
    ],
  },
  {
    titleKey: "Market",
    icon: FiBriefcase,
    items: [{ icon: FiBriefcase, labelKey: "Companies", path: urls.v2.organizations, roles: ALL_ROLES }],
  },
  {
    titleKey: "Procurements",
    icon: FiShoppingCart,
    items: [
      { icon: FiShoppingCart, labelKey: "All procurements", path: urls.v2.sourcingEvents.viewAll, roles: BUYER_ROLES, access: "canViewSourcingEvents" },
      { icon: FiEdit, labelKey: "In progress", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("arbeid"), roles: BUYER_ROLES, badgeKey: "anskArbeid", access: "canViewSourcingEvents" },
      { icon: FiGlobe, labelKey: "Announced", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("kunngjort"), roles: BUYER_ROLES, badgeKey: "anskKunngjort", access: "canViewSourcingEvents" },
      { icon: FiBarChart2, labelKey: "Under evaluation", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("evaluering"), roles: BUYER_ROLES, badgeKey: "anskEvaluering", access: "canViewSourcingEvents" },
      { icon: FiAward, labelKey: "Award and standstill", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("tildeling"), roles: BUYER_ROLES, badgeKey: "anskTildeling", access: "canViewSourcingEvents" },
      { icon: FiArchive, labelKey: "Completed", path: urls.v2.sourcingEvents.viewAll, query: bucketQuery("fullfort"), roles: BUYER_ROLES, badgeKey: "anskFullfort", access: "canViewSourcingEvents" },
      { icon: FiShoppingCart, labelKey: "Find procurements", path: urls.v2.supplier.procurements, roles: ["supplier"] },
      { icon: FiFileText, labelKey: "My bids", path: urls.v2.supplier.bids, roles: ["supplier"] },
      { icon: FiInbox, labelKey: "My invitations", path: urls.v2.supplier.invitations, roles: ["supplier"] },
      { icon: FiUsers, labelKey: "My contributions", path: urls.v2.supplier.contributions, roles: ["supplier"] },
    ],
    action: { labelKey: "New procurement", roles: ["advisor"], kind: "createSourcingEvent", access: "canCreateEvents" },
  },
  // Decided (SM-988): the Library stays its own section, visible to org admins only. Templates and
  // the requirement library are org-admin content: only an admin can create, edit or delete them, so
  // only an admin gets the menu entries. Everyone else still reaches the content where it is used —
  // "Bruk mal" in the wizard and the library search inside each requirement section.
  {
    titleKey: "Library",
    icon: FiGrid,
    access: "isOrgAdmin",
    items: [
      { icon: FiGrid, labelKey: "Templates", path: urls.v2.maler.list, roles: BUYER_ROLES, access: "hasBuyerAccess" },
      { icon: FiBookOpen, labelKey: "Requirement library", path: urls.v2.kravbibliotek, roles: BUYER_ROLES, access: "hasBuyerAccess" },
    ],
  } satisfies NavSection,
  // Decided (SM-988): Contracts and Projects stay separate sections — Contracts is the agreement
  // lifecycle itself, Projects the optional grouping across procurements and agreements (own
  // subscription flag). "Search in contracts" stays an item under Contracts, not its own section.
  {
    titleKey: "Contracts",
    icon: FiFolder,
    items: [
      { icon: FiFolder, labelKey: "All contracts", path: urls.v2.contracts.viewAll, roles: ALL_ROLES, access: "canViewContracts" },
      { icon: FiFileText, labelKey: "Active contracts", path: urls.v2.contracts.active, roles: ALL_ROLES, access: "canViewContracts" },
      { icon: FiCheckSquare, labelKey: "Contract tasks", path: urls.v2.contracts.tasks, roles: ALL_ROLES, access: "canViewContracts" },
      { icon: FiFolder, labelKey: "My contracts", path: urls.v2.contracts.viewAll, roles: ALL_ROLES, access: "viewsContractsAsSupplier" },
      {
        icon: FiSearch,
        labelKey: "Search in contracts",
        path: urls.v2.contracts.search,
        roles: ALL_ROLES,
        access: "canUseContractSearch",
      },
    ],
    action: { labelKey: "New contract", roles: ALL_ROLES, kind: "createContract", access: "canCreateContracts" },
  },
  {
    titleKey: "Projects",
    icon: FiLayers,
    access: "hasProjectAccess",
    items: [
      { icon: FiLayers, labelKey: "All projects", path: urls.v2.projects.viewAll, roles: BUYER_ROLES, access: "canViewProjects" },
    ],
    action: { labelKey: "New project", roles: BUYER_ROLES, kind: "createProject", access: "canCreateProjects" },
  },
];
